import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Image,
  Text,
  VStack,
  LinkBox,
  LinkOverlay,
} from "@chakra-ui/react"

import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogActionTrigger,
} from "../ui/dialog"

import { useQuery } from "@tanstack/react-query"
import { ReportsService } from "@/client"
import type { FeedPublic } from "@/client"

import FeedCardSkeletonList from "@/components/Reports/FeedCardSkeleton"

interface FeedDetailProps {
  feedId: string | null
  isOpen: boolean
  onClose: () => void
}

const FeedDetail = ({ feedId, isOpen, onClose }: FeedDetailProps) => {
  const {
    data: feedDetail,
    isLoading,
  } = useQuery<FeedPublic>({
    queryKey: ["feedDetail", feedId],
    queryFn: () =>
      feedId
        ? ReportsService.readFeed({
            id: feedId,
          })
        : Promise.reject("No feedId"),
    enabled: !!feedId && isOpen,
  })

  return (
    <DialogRoot
      size={{ base: "md", md: "xl" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>피드 상세 정보</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading || !feedDetail ? (
            <FeedCardSkeletonList count={5} />
          ) : (
            <VStack align="start" gap={4}>
              <HStack>
                <Image
                  src={`https://www.google.com/s2/favicons?domain_url=${feedDetail.feed_url}&sz=64`}
                  boxSize="24px"
                  borderRadius="md"
                  alt="favicon"
                />
                <Text fontWeight="bold" wordBreak="break-all">{feedDetail.feed_name}</Text>
              </HStack>

              <Box w="full" bg="gray.50" p={4} borderRadius="md">
                <Text fontSize="sm" color="gray.700" wordBreak="break-all">
                  {feedDetail.feed_description}
                </Text>
              </Box>
              

              <Box w="full">
                <Text fontWeight="semibold" mb={2}>
                  최근 항목
                </Text>
                {feedDetail.items?.length === 0 ? (
                  <Text fontSize="sm" color="gray.400">
                    항목이 없습니다.
                  </Text>
                ) : (
                  feedDetail.items?.map((item: any, i: number) => {
                    const hasImage = item.summary?.includes("<img") ?? false;
                    return (
                    <LinkBox
                      as="article"
                      key={i}
                      mb={3}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      _hover={{ bg: "gray.100" }}
                      transition="background 0.2s"
                    >
                      <HStack align="start" gap={4}>
                        {/* 왼쪽 썸네일 (있을 때만 렌더링) */}
                        {item.videoLink ? (
                          <Box flexShrink={0}>
                            <iframe
                              src={item.videoLink}
                              width="240"
                              height="135"
                              style={{ borderRadius: "6px" }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </Box>
                        ) : item.thumbnail && !hasImage ? (
                          <Image
                            src={item.thumbnail}
                            alt="thumbnail"
                            width="100%"
                            height="100%"
                            maxW="160px"
                            aspectRatio={16 / 9}
                            borderRadius="md"
                            objectFit="cover"
                            flexShrink={0}
                          />
                        ) : null}

                        {/* 오른쪽 텍스트 영역 */}
                        <VStack align="start" gap={1} flex="1">
                          <LinkOverlay
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Text fontWeight="bold" fontSize="sm">
                              {item.title}
                            </Text>
                          </LinkOverlay>

                          <Text fontSize="xs" color="gray.500">
                            {item.published_at}
                          </Text>

                          {item.summary && (
                            <Box
                              fontSize="sm"
                              color="gray.600"
                              dangerouslySetInnerHTML={{
                                __html: item.summary ?? "",
                              }}
                              overflow="hidden"
                              display="-webkit-box"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical",
                              }}
                            />
                          )}
                        </VStack>
                      </HStack>
                    </LinkBox>
                  )})
                )}

              </Box>
            </VStack>
          )}
        </DialogBody>
        <DialogFooter>
          <ButtonGroup>
            <DialogActionTrigger asChild>
              <Button variant="solid">닫기</Button>
            </DialogActionTrigger>
          </ButtonGroup>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default FeedDetail
